import { createContext } from 'react';
import type { Dispatch, SetStateAction } from 'react';
// Types
import { Mode } from './types/Mode';
import type { EventData, TaskData } from '../types';

import { ICalendar } from '@trii/types/dist/Calendar/Calendar';
import { CalendarDialogMode } from './types/CalendarDialogMode';

// Hooks
import { UseCalendarEventsType, useCalendarEventsInitialState } from '../hooks/useCalendarEvents';
import { UseDialog, useDialogInitialState } from '../hooks/useDialog';
import { UseEvent, useEventInitialState } from '../hooks/useEvent';
import { UseCalendarType, useCalendarInitialState } from '../hooks/useCalendar';
import { useCalendarTasksInitialState, UseCalendarTasksType } from '../hooks/useCalendarTasks';
import { UseTask, useTaskInitialState } from '../hooks/useTask';
import { DialogType } from './types/DialogType';

export type CalendarContextType = {
  eventDialog: UseDialog;
  createCalendarDialog: UseDialog;
  event: UseEvent;
  events: UseCalendarEventsType;
  calendar: UseCalendarType;
  dialogMode: Mode;
  handleCreateEvent(eventData?: EventData): void;
  handleSelectEvent(event: any): void;
  handleOpenEventCreate: () => void;
  handleStartEditingEvent: () => void;
  handleSaveUpdatedEvent: () => void;
  handleEndEvent(): void;
  handleDeleteEvent(): void;
  handleResizeEvent(event: any, start: Date | string, end: Date | string): void;
  selectedEvent: any;
  // Snackbar para notificaciones de guardado
  savingSnackbar: {
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'error';
  };
  handleCloseSnackbar: () => void;
  // estado y función para controlar el tipo de diálogo de creación
  dialogType: DialogType;
  // función para establecer el tipo de diálogo de creación
  setDialogType: (value: DialogType) => void;
  // función para abrir el modal para crear una tarea
  handleOpenTaskCreate: () => void;
  // función para abrir el modal de creación arrastrando el click en el calendario
  handleOpenCreateFromSlot: (start: Date, end: Date) => void;
  // función para cerrar el diálogo de creación de calendario
  handleCloseCreateCalendarDialog: () => void;
  // estado para controlar dialog (crear / editar)
  calendarDialogMode: CalendarDialogMode;
  // calendario que se está editando
  editingCalendar: ICalendar | null;
  // función para abrir el diálogo de creación de calendario
  handleOpenCreateCalendarDialog: () => void;
  // función para abrir el diálogo de edición de calendario
  handleOpenEditCalendarDialog: (cal: ICalendar) => void;
  // funciona para enviar el formulario de creación/edición
  handleSubmitCalendar: () => Promise<void>;
  // Tasks
  tasks: UseCalendarTasksType;
  // estado para controlar el calendario activo
  activeCalendarIds: string[];
  // función para establecer el calendario activo
  setActiveCalendarIds: Dispatch<SetStateAction<string[]>>;
  // función para crear una tarea
  handleCreateTask: (taskData?: TaskData) => void;
  // estado y función para manejar la creación de tareas
  task: UseTask;
  // función para seleccionar una tarea (y abrir su modal)
  selectedTask: any;
  // estado para controlar el calendario al que se asigna la tarea que se está creando
  taskCalendarId: string;
  // función para establecer el calendario al que se asigna la tarea que se está creando
  setTaskCalendarId: ((id: string) => void) | Dispatch<SetStateAction<string>>;
  // estado para controlar el calendario al que se asigna el evento que se está creando
  eventCalendarId: string;
  // función para establecer el calendario al que se asigna el evento que se está creando
  setEventCalendarId: ((id: string) => void) | Dispatch<SetStateAction<string>>;
  // flag para trackear si el usuario cambió el color del evento manualmente
  userManuallyChangedColor: boolean;
  // función para marcar que el usuario cambió el color manualmente
  setUserManuallyChangedColor: ((value: boolean) => void) | Dispatch<SetStateAction<boolean>>;
  // calendarios míos
  myCalendars: ICalendar[];
  // calendarios de otros
  otherCalendars: ICalendar[];
  // loading de calendarios
  isLoadingCalendars: boolean;
  // loading de operaciones
  isCreatingEvent: boolean;
  isUpdatingEvent: boolean;
  // isDeletingEvent: boolean; // Not used currently
  isCreatingTask: boolean;
  isUpdatingTask: boolean;
  // isDeletingTask: boolean; // Not used currently
  // función para editar una tarea
  handleSaveUpdatedTask: () => void;
  // función para eliminar una tarea
  handleDeleteTask: () => void;
  // función para alternar estado completado de tarea
  handleToggleTaskCompleted: () => void;
  // función para duplicar un evento o tarea
  handleDuplicateItem: () => void;
  // vista actual del calendario (month, week, day)
  calendarView: 'month' | 'week' | 'day';
  // función para cambiar la vista del calendario
  setCalendarView: (view: 'month' | 'week' | 'day') => void;
  // fecha actual visible en el calendario
  calendarDate: Date;
  // función para cambiar la fecha visible en el calendario
  setCalendarDate: (date: Date) => void;
  // función para navegar a una fecha específica en vista día
  navigateToDate: (date: Date) => void;
  // Modal de confirmación para eliminar eventos/tareas normales
  confirmDeleteDialog: {
    open: boolean;
    handleClose: () => void;
    itemType: 'event' | 'task';
  };
  // función para confirmar la eliminación de eventos/tareas normales
  handleConfirmDelete: () => void;
  // Modal de confirmación para eliminar eventos recurrentes
  deleteRecurringDialog: {
    open: boolean;
    handleClose: () => void;
  };
  // función para confirmar la eliminación de eventos recurrentes
  handleConfirmDeleteRecurring: (deleteAll: boolean) => void;
  // Modal de confirmación para editar eventos recurrentes
  editRecurringDialog: {
    open: boolean;
    handleClose: () => void;
  };
  // función para confirmar la edición de eventos recurrentes
  handleConfirmEditRecurring: (editAll: boolean) => void;
  // flag para ocultar tabs en creación (usado para creación rápida desde slot)
  hiddenTabs: boolean;
};

export const CalendarContext = createContext<CalendarContextType>({
  eventDialog: useDialogInitialState,
  createCalendarDialog: useDialogInitialState,
  event: useEventInitialState,
  events: useCalendarEventsInitialState,
  calendar: useCalendarInitialState,
  dialogMode: 'view',
  handleCreateEvent: () => {},
  handleSelectEvent: () => {},
  handleOpenEventCreate: () => {},
  handleStartEditingEvent: () => {},
  handleSaveUpdatedEvent: () => {},
  handleEndEvent: () => {},
  handleDeleteEvent: () => {},
  handleResizeEvent: () => {},
  selectedEvent: {},
  savingSnackbar: { open: false, message: '', severity: 'info' },
  handleCloseSnackbar: () => {},
  dialogType: 'event',
  setDialogType: () => {},
  handleOpenTaskCreate: () => {},
  handleOpenCreateFromSlot: () => {},
  handleCloseCreateCalendarDialog: () => {},
  calendarDialogMode: 'create',
  editingCalendar: null,
  handleOpenCreateCalendarDialog: () => {},
  handleOpenEditCalendarDialog: () => {},
  handleSubmitCalendar: async () => {},
  tasks: useCalendarTasksInitialState,
  activeCalendarIds: [],
  setActiveCalendarIds: () => {},
  handleCreateTask: () => {},
  task: useTaskInitialState,
  selectedTask: null,
  taskCalendarId: '',
  setTaskCalendarId: () => {},
  eventCalendarId: '',
  setEventCalendarId: () => {},
  userManuallyChangedColor: false,
  setUserManuallyChangedColor: () => {},
  myCalendars: [],
  otherCalendars: [],
  isLoadingCalendars: false,
  isCreatingEvent: false,
  isUpdatingEvent: false,
  // isDeletingEvent: false, // Not used currently
  isCreatingTask: false,
  isUpdatingTask: false,
  // isDeletingTask: false, // Not used currently
  handleSaveUpdatedTask: () => {},
  handleDeleteTask: () => {},
  handleToggleTaskCompleted: () => {},
  handleDuplicateItem: () => {},
  calendarView: 'month',
  setCalendarView: () => {},
  calendarDate: new Date(),
  setCalendarDate: () => {},
  navigateToDate: () => {},
  confirmDeleteDialog: {
    open: false,
    handleClose: () => {},
    itemType: 'event',
  },
  handleConfirmDelete: () => {},
  deleteRecurringDialog: {
    open: false,
    handleClose: () => {},
  },
  handleConfirmDeleteRecurring: () => {},
  editRecurringDialog: {
    open: false,
    handleClose: () => {},
  },
  handleConfirmEditRecurring: () => {},
  hiddenTabs: false,
});
